/**
 * Cross-tab session-list invalidation via long-lived SSE.
 *
 * Mounted once at the authenticated shell level. The backend pushes a
 * `session_changed` event whenever any run halts/finishes — covers the
 * `awaiting_user_input` transitions that wouldn't otherwise reach a tab
 * not currently watching the chat's own stream (background runs, sidebar
 * after navigating away from a chat).
 *
 * Uses fetch+manual SSE parsing rather than EventSource because the
 * endpoint requires the JWT in `Authorization`, which EventSource can't
 * carry. Pattern mirrors `useSessionAttach`.
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/shared/api";

export function useSessionEvents() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const ac = new AbortController();
    let stopped = false;

    const run = async () => {
      // Reconnect with a small backoff if the stream drops (tab sleep,
      // proxy timeout). No cap — the page-level lifetime owns this.
      let backoffMs = 500;
      while (!stopped) {
        try {
          const resp = await apiFetch("/api/events", { signal: ac.signal });
          if (!resp.ok || !resp.body) {
            await new Promise((r) => setTimeout(r, backoffMs));
            backoffMs = Math.min(backoffMs * 2, 10_000);
            continue;
          }
          backoffMs = 500;
          const reader = resp.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (!stopped) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const blocks = buffer.split("\n\n");
            buffer = blocks.pop() ?? "";
            for (const block of blocks) {
              for (const line of block.split("\n")) {
                if (!line.startsWith("data: ")) continue;
                try {
                  const evt = JSON.parse(line.slice(6));
                  if (evt?.type === "session_changed") {
                    queryClient.invalidateQueries({ queryKey: ["sessions"] });
                  }
                } catch {
                  /* skip malformed */
                }
              }
            }
          }
        } catch {
          /* AbortError on unmount, network blip — fall through to retry */
        }
        if (!stopped) {
          await new Promise((r) => setTimeout(r, backoffMs));
          backoffMs = Math.min(backoffMs * 2, 10_000);
        }
      }
    };

    run();

    return () => {
      stopped = true;
      ac.abort();
    };
  }, [queryClient]);
}
